import { Component, OnInit, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';

import { DeviceDetectorService } from 'ngx-device-detector';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { PaymentService } from '../../services/payment.service';

import {
  AutoPaymentConfirmationDialogData,
  PaymentType,
  BankAccountType,
  AutoPayCreditCardPaymentInformation,
  AutoPayACHPaymentInformation,
  CancelRecurringPaymentInformation
} from '../../models/payment';
import { BraintreeService } from 'src/app/services/braintree.service';

@Component({
  selector: 'app-payment-automatic-confirmation-dialog',
  templateUrl: './payment-automatic-confirmation-dialog.component.html',
  styleUrls: ['./payment-automatic-confirmation-dialog.component.scss']
})
export class PaymentAutomaticConfirmationDialogComponent implements OnInit {
  private _handleError: HandleError;
  private _logging = environment.logging;

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showSpinner = false;
  showError = false;
  showCloseButton = true;

  paymentType = PaymentType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AutoPaymentConfirmationDialogData,
    private _errorHandler: ErrorHandler,
    private _paymentService: PaymentService,
    private _deviceService: DeviceDetectorService,
    public _braintreeService: BraintreeService,
    public dialogRef: MatDialogRef<PaymentAutomaticConfirmationDialogComponent>
  ) {
    this._handleError = _errorHandler.createHandleError('PaymentAutomaticConfirmationDialogComponent');
    if (this.data.autoPaymentDetail.isAutoPaymentSetup) {
      this.setupAutoPayment();
    } else if (this.data.autoPaymentDetail.isAutoPaymentCancel) {
      this.cancelAutoPayment();
    } else {
      this._handleWorkflowError('Auto Payment request is not for setup or cancel.');
      this._handleError('constructor', 'Auto Payment request is not for setup or cancel.', this.data);
    }
  }

  ngOnInit() {}

  setupAutoPayment(): void {
    this.showSpinner = true;

    if (this.data.autoPaymentDetail.paymentType === PaymentType.ElectronicCheck) {
      const autoPayACHPaymentInformation: AutoPayACHPaymentInformation = {
        noteId: this.data.autoPaymentDetail.noteId,
        fullName: this.data.autoPaymentDetail.name,
        bankAccountNumber: this.data.autoPaymentDetail.bankAccountNumber,
        bankAccountType: this.data.autoPaymentDetail.bankAccountType === BankAccountType.Checking ? 'C' : 'S',
        bankRoutingNumber: this.data.autoPaymentDetail.bankRoutingNumber,
        userName: this.data.autoPaymentDetail.userName,
        confirmationNumbers: this.data.autoPaymentDetail.confirmationNumbers
      };

      this._paymentService.setupAutoPayACHPayment(autoPayACHPaymentInformation).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('PaymentAutomaticConfirmationDialogComponent.setupAutoPayment.setupAutoPayACHPayment :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('PaymentAutomaticConfirmationDialogComponent.setupAutoPayment.setupAutoPayACHPayment :: result = ' + result);
          }

          this.data.autoPaymentDetail.confirmationCode = result !== null ? result.toString() : null;

          if (result !== null && Number(result) > 0) {
            this.data.paymentOk = true;
          } else {
            this._handleWorkflowError('ACH Automatic Payment could not be applied!');
          }
          this.autoPaymentComplete();
        },
        error => {
          this._handleWorkflowError('ACH Automatic Payment could not be applied!');
          this._handleError('setupAutoPayment.setupAutoPayACHPayment', error, this.data);
          this.autoPaymentComplete();
        }
      );
    } else {
      const autoPayCreditCardPaymentInformation: AutoPayCreditCardPaymentInformation = {
        participantId: this.data.autoPaymentDetail.participantId,
        noteId: this.data.autoPaymentDetail.noteId,
        paymentAmount: this.data.autoPaymentDetail.paymentAmount,
        paymentDueDay: this.data.autoPaymentDetail.paymentDueDay,
        loanProgram: this.data.autoPaymentDetail.loanProgram,

        // servicingCompany never set will always be null
        servicingCompany: this.data.autoPaymentDetail.servicingCompany,
        fullName: this.data.autoPaymentDetail.name,
        addressLine1: this.data.autoPaymentDetail.addressLine1,
        city: this.data.autoPaymentDetail.city,
        state: this.data.autoPaymentDetail.state,
        zipCode: this.data.autoPaymentDetail.zipCode,
        userName: this.data.autoPaymentDetail.userName,
        paymentRef: this.data.autoPaymentDetail.paymentRef,
        confirmationCode: this.data.autoPaymentDetail.confirmationCode,

        // Braintree tokenized response
        nonce: this.data.autoPaymentDetail.nonce,
        partiFirstName: this.data.autoPaymentDetail.partiFirstName,
        partiLastName: this.data.autoPaymentDetail.partiLastName,
        partiEmail: this.data.autoPaymentDetail.partiEmail,
        deviceData: this.data.autoPaymentDetail.deviceData,
        creditCardBin: this.data.autoPaymentDetail.creditCardBin,
        creditCardType: this.data.autoPaymentDetail.creditCardType,
        lastFourCreditCard: this.data.autoPaymentDetail.lastFourCreditCard,
        creditCardExpMonth: this.data.autoPaymentDetail.creditCardExpMonth,
        creditCardExpYear: this.data.autoPaymentDetail.creditCardExpYear,
        brainTreePaymentType: this.data.autoPaymentDetail.brainTreePaymentType,
        brainTreeLenderProgramCode: this.data.autoPaymentDetail.brainTreeLenderProgramCode,
      };

      this._paymentService.setupAutoPayCreditCardPayment(autoPayCreditCardPaymentInformation).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('PaymentAutomaticConfirmationDialogComponent.setupAutoPayment.setupAutoPayCreditCardPayment :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('PaymentAutomaticConfirmationDialogComponent.setupAutoPayment.setupAutoPayCreditCardPayment :: result = ' + result);
          }

          this.data.autoPaymentDetail.confirmationCode = result !== null ? result.toString() : null;

          // if (Number(result) === 0) {
          if (result !== null && Number(result) >= 0) {
            this.data.paymentOk = true;
          } else {
            this._handleWorkflowError('Credit Card Automatic Payment could not be applied!');
          }
          this.autoPaymentComplete();
        },
        error => {
          this.data.paymentOk = false;
          this._handleWorkflowError('Credit Card Automatic Payment could not be applied!');
          this._handleError('setupAutoPayment.setupAutoPayCreditCardPayment', error, this.data);
          this.autoPaymentComplete();
        }
      );
    }
  }

  cancelAutoPayment(): void {
    this.showSpinner = true;

    const cancelRecurringPaymentInformation: CancelRecurringPaymentInformation = {
      participantId: this.data.autoPaymentDetail.participantId,
      noteId: this.data.autoPaymentDetail.noteId,
      userName: this.data.autoPaymentDetail.userName,
      loanProgram: this.data.autoPaymentDetail.loanProgram
    };

    this._paymentService.cancelAutoPay(cancelRecurringPaymentInformation).subscribe(
      result => {
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticConfirmationDialogComponent.cancelAutoPayment.cancelAutoPay :: result = ' + result);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('PaymentAutomaticConfirmationDialogComponent.cancelAutoPayment.cancelAutoPay :: result = ' + result);
        }

        this.data.autoPaymentDetail.confirmationCode = result !== null ? result.toString() : null;

        if (result === 0) {
          this.data.paymentOk = true;
        } else {
          this._handleWorkflowError('Automatic Payment could not be canceled!');
        }
        this.autoPaymentComplete();
      },
      error => {
        this._handleWorkflowError('Automatic Payment could not be canceled!');
        this._handleError('cancelAutoPayment.cancelAutoPay', error, this.data);
        this.autoPaymentComplete();
      }
    );
  }

  autoPaymentComplete(): void {
    this.showSpinner = false;
  }

  maskCreditCardNumber(creditCardNumber: string): string {
    return creditCardNumber.replace(/\d(?=\d{4})/g, '*');
  }

  onCloseClick() {
    this.dialogRef.close(this.data);
  }

  isMobileDevice(): boolean {
    return !this._deviceService.isDesktop();
  }

  private _handleWorkflowError(errorMessage: string): void {
    this.showError = true;
    this.data.paymentOk = false;
    this.data.paymentError = errorMessage;
  }
}
